import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMagnifyingGlass,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import {
  COLUMNS,
  cellColor,
  displayValue,
  getMatchingFields,
  groupKeyOf,
  hasPendingPairs,
  memberName,
} from "./duplicateMatching";

function DuplicateUsersMobileList({
  groups,
  loading,
  error,
  searchQuery,
  onSearchChange,
  showDismissed,
  onShowDismissedChange,
  selection,
  onToggleSelect,
  isSelectable,
  selectionBlockedReason,
  onCompare,
  onDismiss,
  onRestore,
  pairActionPending,
  page,
  totalPages,
  totalResults,
  startRecord,
  endRecord,
  onPageChange,
}) {
  return (
    <div className="bg-white rounded-lg shadow-lg p-4">
      <h1 className="text-base font-semibold mb-3">Duplicate Users</h1>

      <div className="relative mb-3">
        <FontAwesomeIcon
          icon={faMagnifyingGlass}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"
        />
        <input
          type="text"
          placeholder="Search by Name/Email"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-red-400"
        />
      </div>

      <label className="flex items-center gap-2 text-sm text-gray-700 mb-3 cursor-pointer">
        <input
          type="checkbox"
          checked={showDismissed}
          onChange={(e) => onShowDismissedChange(e.target.checked)}
          className="accent-red-500"
        />
        Show dismissed
      </label>

      {loading ? (
        <div className="text-center py-10 text-gray-500">Loading...</div>
      ) : error ? (
        <div className="text-center py-10 text-red-500">{error}</div>
      ) : groups.length === 0 ? (
        <div className="text-center py-10 text-gray-400">No records found.</div>
      ) : (
        <>
          <div className="space-y-4">
            {groups.map((group) => {
              const key = groupKeyOf(group);
              const matches = getMatchingFields(group.members);
              const isActiveGroup = selection.groupKey === key;
              const canAct = isActiveGroup && selection.userIds.length === 2;

              return (
                <div
                  key={key}
                  className="border border-gray-300 rounded-md overflow-hidden"
                >
                  <div className="bg-gray-100 px-3 py-2 border-b border-gray-200">
                    <p className="font-semibold text-gray-900 uppercase text-sm">
                      {group.firstName} {group.lastName}
                    </p>
                    <p className="text-xs text-gray-600">
                      {group.members?.length ?? 0} accounts
                      {!hasPendingPairs(group) && (
                        <span className="ml-2 px-2 py-0.5 rounded-full bg-gray-300 text-gray-700">
                          Dismissed
                        </span>
                      )}
                    </p>
                    {canAct && (
                      <div className="mt-2 flex gap-2">
                        <button
                          onClick={onDismiss}
                          disabled={pairActionPending}
                          className="flex-1 px-3 py-2 border border-gray-400 bg-white text-gray-700 rounded text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Not a duplicate
                        </button>
                        <button
                          onClick={onCompare}
                          className="flex-1 px-3 py-2 bg-red-500 text-white rounded text-xs hover:bg-red-600"
                        >
                          Compare &amp; Merge
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="divide-y divide-gray-200">
                    {(group.members ?? []).map((member) => {
                      const checked =
                        isActiveGroup &&
                        selection.userIds.includes(member.userId);
                      return (
                        <div key={member.userId} className="p-3">
                          <label className="flex items-center gap-2 mb-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={!isSelectable(group, member.userId)}
                              title={selectionBlockedReason(
                                group,
                                member.userId,
                              )}
                              onChange={() =>
                                onToggleSelect(group, member.userId)
                              }
                              className="accent-red-500 disabled:cursor-not-allowed"
                            />
                            <span className="text-sm font-medium text-gray-800">
                              {member.firstName} {member.lastName}
                            </span>
                          </label>

                          <div className="grid grid-cols-2 gap-2">
                            {COLUMNS.map((col) => (
                              <div
                                key={col.field}
                                className={`border rounded px-2 py-1 min-w-0 ${cellColor(
                                  matches[col.field],
                                )}`}
                              >
                                <p className="text-[10px] uppercase tracking-wide text-gray-500">
                                  {col.label}
                                </p>
                                <p className="text-sm text-gray-900 break-words">
                                  {displayValue(member, col.field)}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {(group.dismissedPairs?.length ?? 0) > 0 && (
                    <div className="bg-gray-50 border-t border-gray-200 px-3 py-2">
                      <p className="text-xs font-semibold uppercase text-gray-600 mb-1">
                        Dismissed pairs
                      </p>
                      <ul className="space-y-2">
                        {group.dismissedPairs.map((pair) => (
                          <li
                            key={`${pair.lowerUserId}-${pair.higherUserId}`}
                            className="flex items-center justify-between gap-2 text-xs text-gray-700"
                          >
                            <span className="italic min-w-0 break-words">
                              {memberName(group, pair.higherUserId)} ↔{" "}
                              {memberName(group, pair.lowerUserId)}
                            </span>
                            <button
                              onClick={() => onRestore(group, pair)}
                              disabled={pairActionPending}
                              className="shrink-0 px-2 py-1 border border-gray-400 bg-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Restore
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {totalResults > 0 && (
            <div className="mt-4 pt-3 border-t border-gray-100">
              <p className="text-xs text-gray-500 text-center mb-2">
                Showing {startRecord}–{endRecord} of {totalResults}
              </p>
              <div className="flex items-center justify-center gap-2">
                <button
                  onClick={() => onPageChange((p) => p - 1)}
                  disabled={page === 1}
                  className="w-8 h-8 flex items-center justify-center rounded border border-gray-200 text-gray-600 disabled:text-gray-300 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  <FontAwesomeIcon icon={faChevronLeft} className="text-xs" />
                </button>
                <span className="text-sm text-gray-700">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => onPageChange((p) => p + 1)}
                  disabled={page === totalPages}
                  className="w-8 h-8 flex items-center justify-center rounded border border-gray-200 text-gray-600 disabled:text-gray-300 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  <FontAwesomeIcon icon={faChevronRight} className="text-xs" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default DuplicateUsersMobileList;
